import React, {useEffect, useMemo, useRef, useState} from 'react';
import { Icon } from '../../components/Icons';
import { useKeyLocation } from '../../FunctionalAPI';
import { getActiveLanguage, T } from '../../Translate';
import { PageID } from '../../utils/PageLinking';
import { PageProps, usePageHeader } from '../Page';
import { PublicLink } from '../PublicViewPersonStory/PublicButton';
import styles from './index.module.scss';
import storyStyles from '../PublicViewPersonStory/index.module.scss';
import { Card, Col, Row } from "react-bootstrap";
import ImageCarrousel from '../PublicViewPersonStory/ImageCarrousel';
import { IPersonStory2 } from '../../models/PersonStory2';
import { MarkdownView } from '../../components/MarkdownView';
import PageLink from '../../components/PageLink';
import storyViewStyles from '../PublicViewPersonStory/StoryView.module.scss';
import { IKeyLocation } from '../../models/KeyLocation';
import { MapMarkerInput, MapMarkerInputMap, MapMarkerInputValue } from '../../components/inputs/MapMarkerInputMB';
import { Carousel } from 'react-bootstrap';
import {FullscreenImageView} from "../../components/FullscreenImageView";
interface ViewKeyLocationPageProps extends PageProps {
  id: string;
}

const namelistLinks: {[key: string]: string} = {
  'nl': 'https://www.inflandersfields.be/nl/kenniscentrum/namenlijst/',
  'en': 'https://www.inflandersfields.be/en/kenniscentrum-e/namenlijst-e',
  'fr': 'https://www.inflandersfields.be/fr/kenniscentrum-f/namenlijst-f',
  'de': 'https://www.inflandersfields.be/de/kenniscentrum-d/namenlijst-d'
};

const LocationWithPhotos: ('photos'|'stories')[] = ['photos', 'stories'];

export const ViewKeyLocationPage = (props: ViewKeyLocationPageProps) => {
  const { id, loading } = props;

  const keyLocation = useKeyLocation(loading, id, LocationWithPhotos);
  const keyStories = keyLocation?.stories || [];

  const subtitles = useMemo(() => [T('page.keyLocation.public.subtitle1'), T('page.keyLocation.public.subtitle2')], []);
  usePageHeader(
    PageID.PublicViewKeyLocation,
    id,
    undefined,
    T('page.keyLocation.public.title'),
    '',
    undefined,
    undefined,
    undefined,
    subtitles
  );

  return <>
    <div className={styles.header}>
      <div className='container'>
        <div style={{ float: 'right' }}>
          <PublicLink style={{ marginLeft: 10 }} url={`https://www.inflandersfields.be/lostkeys`}>
            {T('page.keyLocation.public.extraInformation')}
          </PublicLink>
        </div>
      </div>
    </div>
    {keyLocation && <KeyLocationView keyLocation={keyLocation} />}

    <div className='container'>
      <div className={storyStyles.timeline}>
        {keyStories.filter(story => story.published_date).map(story => (
          <StoryView key={story._id} story={story} />
        ))}
      </div>
    </div>
  </>;
}

interface KeyLocationViewProps {
  keyLocation: IKeyLocation;
}




function KeyLocationView(props: KeyLocationViewProps) {
    const { keyLocation } = props;
    const [activeIndex, setActiveIndex] = useState(0);
    const [fullscreenOpen, setFullscreenOpen] = useState(false);
    const mapRef = useRef<mapboxgl.Map | null>(null);


    const carrouselImages = useMemo(() => {
        return (keyLocation?.photos || []).map(photo => ({
            url: photo.urls.preview || photo.urls.original,
            fullSizeUrl: photo.urls.original
        }));
    }, [keyLocation]);

    const currentUrl = activeIndex > 0 ?  carrouselImages[activeIndex - 1].fullSizeUrl : null;

    const totalItems = 1 + carrouselImages.length; // 1 for map + images

    const handlePrev = () => {
        setActiveIndex((activeIndex - 1 + totalItems) % totalItems);
    };

    const handleNext = () => {
        setActiveIndex((activeIndex + 1) % totalItems);
    };

    const handleClicked = () => activeIndex > 0 && setFullscreenOpen(true);
    const handleClickedFullscreenClose = () => setFullscreenOpen(false);

    // Resize map when carousel slides to it
    useEffect(() => {
        if (activeIndex === 0 && mapRef.current) {
            // Small delay to ensure the carousel transition is complete
            setTimeout(() => {
                mapRef.current?.resize();
            }, 100);
        }
    }, [activeIndex]);

    const handleMapReady = (map: mapboxgl.Map) => {
        mapRef.current = map;
    };

    return (
        <div className={storyViewStyles.story}>
            <div className='container'>
                <div className={storyViewStyles.storyWrapper}>
                    <div className={storyViewStyles.storyLine} />
                    <div className={storyViewStyles.storyIconWrapper}>
                        <div className={storyViewStyles.storyIcon}>
                            <i className={Icon.Key} />
                        </div>
                    </div>
                    <div className={storyViewStyles.storyContent} style={{padding: '1em', width: '100%'}}>
                        <div style={{"margin": "0.6rem 0.6rem 1.2rem 0.6rem",
                            "fontSize": "1.0rem",
                            "fontWeight": "normal"}}>
                            <h2 style={{"fontFamily": "'Fjalla One', arial, helvetica, sans-serif", "letterSpacing": "1px"}}>{keyLocation.name}</h2>
                            <p >
                                {keyLocation ? keyLocation.notes[getActiveLanguage()] || "" : ""}
                            </p>
                        </div>
                        <Carousel
                            interval={null}
                            indicators={false}
                            controls={false}
                            activeIndex={activeIndex}
                            onSelect={(selectedIndex) => setActiveIndex(selectedIndex)}
                            style={{ height: '400px' }}
                            onClick={handleClicked}
                        >
                            {/* First item: Map */}
                            <Carousel.Item>
                                <div style={{ height: '400px', width: '100%', backgroundColor: '#343233' }}>
                                    <MapMarkerInputMap
                                        containerElement={<div style={{ height: '100%', width: '100%' }} />}
                                        mapElement={<div style={{ height: '100%' }} />}
                                        readOnly
                                        position={MapMarkerInputValue.fromPosition(keyLocation.position)}
                                        defaultZoom={15}
                                        onChange={() => {}}
                                        onMapReady={handleMapReady}
                                    />
                                </div>
                            </Carousel.Item>

                            {/* Remaining items: Images */}
                            {carrouselImages.map((image, index) => (
                                <Carousel.Item key={index}>
                                    <div style={{
                                        height: '400px',
                                        width: '100%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        padding: '1em',
                                    }}>
                                        <img
                                            src={image.fullSizeUrl || ''}
                                            alt={`Photo ${index + 1}`}
                                            style={{
                                                maxHeight: "100%",
                                                maxWidth: '100%',
                                                width: '100%',
                                                height: 'auto',
                                                aspectRatio: '16/9',
                                                objectFit: 'cover',
                                            }}
                                        />
                                    </div>
                                </Carousel.Item>
                            ))}
                        </Carousel>

                        {/* Custom navigation with arrows and dots */}
                        <div style={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginTop: '10px'
                        }}>
                            {/* Previous button */}
                            <div
                                onClick={handlePrev}
                                style={{
                                    fontSize: '32px',
                                    cursor: 'pointer',
                                    paddingLeft: '1rem',
                                    paddingRight: '1rem'
                                }}
                            >
                                <i className={Icon.ChevronLeftLight} />
                            </div>

                            {/* Dot indicators */}
                            <div style={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                {Array.from({ length: totalItems }).map((_, index) => (
                                    <div
                                        key={index}
                                        onClick={() => setActiveIndex(index)}
                                        style={{
                                            width: '10px',
                                            height: '10px',
                                            border: '1px solid white',
                                            borderRadius: '5px',
                                            margin: '5px',
                                            backgroundColor: index === activeIndex ? 'white' : 'transparent',
                                            cursor: 'pointer'
                                        }}
                                    />
                                ))}
                            </div>

                            {/* Next button */}
                            <div
                                onClick={handleNext}
                                style={{
                                    fontSize: '32px',
                                    cursor: 'pointer',
                                    paddingLeft: '1rem',
                                    paddingRight: '1rem'
                                }}
                            >
                                <i className={Icon.ChevronRightLight} />
                            </div>

                            {fullscreenOpen && currentUrl && (
                                <FullscreenImageView src={currentUrl} onClick={handleClickedFullscreenClose} />
                            )}

                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

interface StoryViewProps {
  story: IPersonStory2;
}

function StoryView(props: StoryViewProps) {
  const { story } = props;

  return (
    <div className={storyStyles.event}>
      <div className={storyStyles.eventLineTop} />
      <div className={storyStyles.eventLineBottom} />
      <div className={storyStyles.eventIconWrapper}>
        <div className={storyStyles.eventIcon}>
          <i className={Icon.User} />
        </div>
      </div>
      <div className={storyStyles.eventInfo}>
        <Card style={{ marginBottom: 0 }}>
          <Card.Header>
            <h4 style={{fontSize: '1.1rem', "fontFamily": "'Fjalla One', arial, helvetica, sans-serif", "letterSpacing": "0.9px"}}>
              {story.first_name} {story.family_name}
            </h4>
          </Card.Header>
          <Card.Body style={{ paddingTop: 0, display: 'flex', flexDirection: 'column' }}>
            <div className={styles.storyPreview}>
              {story.story_text?.[getActiveLanguage()]?.story_part_1 || ''}
            </div>
            <div style={{textAlign: 'right'}}>
              <PageLink page={PageID.PublicViewPersonStory} param={story._id}>
                {T('page.keyLocation.public.readStory')}
              </PageLink>
            </div>
          </Card.Body>
        </Card>
      </div>
    </div>
  );
}